/**
 * attribution.ts
 *
 * Captures UTM parameters, click IDs, referrer, URL and user agent
 * at form submission time. Persists to sessionStorage so UTMs survive
 * single-page navigation (e.g., user lands on homepage with ?gclid=...,
 * navigates to contact page, submits form — UTMs still captured).
 */

export interface AttributionPayload {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  gclid?: string;       // Google Ads click ID (auto-tagged by Google)
  fbclid?: string;      // Facebook/META click ID (auto-tagged by META)
  referrer?: string;    // document.referrer
  url?: string;         // window.location.href at time of page load
  userAgent?: string;   // navigator.userAgent
}

const SESSION_KEY = 'propinvest_attribution';

/**
 * Should be called on every page load/route change.
 * If the current URL has UTM params or click IDs, stores them in sessionStorage.
 * This preserves attribution across SPA navigation.
 */
export function trackAttributionFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const hasSignals =
    params.has('utm_source') || params.has('utm_medium') ||
    params.has('gclid') || params.has('fbclid');

  if (hasSignals) {
    const fresh: AttributionPayload = {
      utmSource: params.get('utm_source') ?? undefined,
      utmMedium: params.get('utm_medium') ?? undefined,
      utmCampaign: params.get('utm_campaign') ?? undefined,
      utmContent: params.get('utm_content') ?? undefined,
      utmTerm: params.get('utm_term') ?? undefined,
      gclid: params.get('gclid') ?? undefined,
      fbclid: params.get('fbclid') ?? undefined,
      referrer: document.referrer || undefined,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(fresh));
    } catch {
      // sessionStorage unavailable (private mode edge case)
    }
  }
}

/**
 * Call this just before submitting a form.
 * Returns the best available attribution data — prefers stored UTMs
 * (from when the user first landed on the site) over the current URL.
 */
export function captureAttribution(): AttributionPayload {
  // Try to get stored attribution (from landing page)
  let stored: AttributionPayload = {};
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) stored = JSON.parse(raw) as AttributionPayload;
  } catch {
    // ignore
  }

  // Current page URL params (may differ from landing page)
  const params = new URLSearchParams(window.location.search);

  return {
    // Prefer stored (landing page) UTMs — they're more accurate for attribution
    utmSource: stored.utmSource ?? params.get('utm_source') ?? undefined,
    utmMedium: stored.utmMedium ?? params.get('utm_medium') ?? undefined,
    utmCampaign: stored.utmCampaign ?? params.get('utm_campaign') ?? undefined,
    utmContent: stored.utmContent ?? params.get('utm_content') ?? undefined,
    utmTerm: stored.utmTerm ?? params.get('utm_term') ?? undefined,
    gclid: stored.gclid ?? params.get('gclid') ?? undefined,
    fbclid: stored.fbclid ?? params.get('fbclid') ?? undefined,
    // Always use current values for these
    referrer: stored.referrer ?? document.referrer ?? undefined,
    url: stored.url ?? window.location.href,
    userAgent: navigator.userAgent,
  };
}
